import {
  AlertTriangle,
  BookOpen,
  Check,
  Clock,
  Download,
  File,
  FileText,
  Folder,
  MessageCircle,
  Plus,
  Search,
  Send,
  Shield,
  Target,
  Trash2,
  TrendingUp,
  Upload,
  Users,
  X,
  Zap
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { useTenant } from '../../contexts/TenantContext';
import { chatAPI } from '../../services/chatAPI';
import { ChatArtifact, ChatMessage, ChatSession } from '../../types/chat';

const AskJarvis: React.FC = () => {
  const { user } = useAuth();
  const { currentProject } = useProject();
  const { tenantData } = useTenant();
  const [isOpen, setIsOpen] = useState(false);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSession, setCurrentSession] = useState<ChatSession | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showDocuments, setShowDocuments] = useState(false);
  const [userDocuments, setUserDocuments] = useState<any[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<number[]>([]);
  const [documentSearchQuery, setDocumentSearchQuery] = useState('');
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [documentRAGStatus, setDocumentRAGStatus] = useState<{[key: number]: boolean}>({});
  const [buttonPosition, setButtonPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, startX: 0, startY: 0 });
  const [mouseDown, setMouseDown] = useState(false);
  const [availableModels, setAvailableModels] = useState<any[]>([]);
  const [selectedModel, setSelectedModel] = useState<string>('claude-4-sonnet');
  const [showModelSelector, setShowModelSelector] = useState(false);
  const pendingArtifactsRef = useRef<ChatArtifact[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const predefinedPrompts = [
    {
      icon: FileText,
      title: "Generate SOP",
      description: "Create Standard Operating Procedures",
      prompt: "Generate a comprehensive SOP for the loan origination process based on our uploaded documents"
    },
    {
      icon: Shield,
      title: "Risk Analysis",
      description: "Analyze risks and vulnerabilities",
      prompt: "Perform a detailed risk analysis on our current processes and identify potential vulnerabilities"
    },
    {
      icon: AlertTriangle,
      title: "Compliance Check",
      description: "Review regulatory compliance",
      prompt: "Check our processes for Basel III and regulatory compliance requirements"
    },
    {
      icon: TrendingUp,
      title: "Process Optimization",
      description: "Suggest improvements",
      prompt: "Analyze our workflows and suggest process optimizations to improve efficiency"
    },
    {
      icon: Users,
      title: "Stakeholder Mapping",
      description: "Identify key stakeholders",
      prompt: "Create a stakeholder map for our banking processes and define roles and responsibilities"
    },
    {
      icon: Target,
      title: "Control Matrix",
      description: "Generate control framework",
      prompt: "Create a risk control matrix with specific control measures for identified risks"
    }
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Load available AI models on mount
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const models = await chatAPI.getAvailableModels();
        // Filter to show only enabled models
        const enabledModels = models.filter(m => m.available);
        setAvailableModels(enabledModels);

        // Set default to Claude 4 Sonnet if available, otherwise first enabled model
        const defaultModel = enabledModels.find(m => m.id === 'claude-4-sonnet') || enabledModels[0];
        if (defaultModel) {
          setSelectedModel(defaultModel.id);
        }

        console.log(`✅ Loaded ${enabledModels.length} available AI models`);
      } catch (error) {
        console.error('Failed to load AI models:', error);
        toast.error('Failed to load AI models');
      }
    };

    fetchModels();
  }, []);

  // Load sessions and documents when chat opens
  useEffect(() => {
    if (isOpen) {
      loadSessions();
      loadDocuments();
    }
  }, [isOpen]);

  // Reload sessions and documents when project changes
  useEffect(() => {
    if (isOpen && currentProject) {
      console.log('🔄 Project changed, reloading chat data for project:', currentProject.id);

      // Clear current session and messages when switching projects
      setCurrentSession(null);
      setMessages([]);
      setSelectedDocuments([]);

      // Reload sessions and documents for new project
      loadSessions();
      loadDocuments();
    }
  }, [currentProject?.id, isOpen]);

  // Load messages when session changes
  useEffect(() => {
    if (currentSession) {
      // Clear messages immediately when switching sessions to prevent cross-contamination
      setMessages([]);

      // Then load the new session's messages
      loadMessages();

      // Load session's active documents
      if (currentSession.active_documents) {
        setSelectedDocuments(currentSession.active_documents);
      } else {
        setSelectedDocuments([]);
      }
    } else {
      // If no session, clear messages
      setMessages([]);
      setSelectedDocuments([]);
    }
  }, [currentSession?.id]); // Only trigger when session ID changes, not when session object changes

  // Sync selected documents with session when they change
  useEffect(() => {
    const syncDocumentsWithSession = async () => {
      if (!currentSession) return;

      // Get current session's active documents
      const currentActiveDocuments = currentSession.active_documents || [];

      // Check if there are any changes
      const hasChanges =
        selectedDocuments.length !== currentActiveDocuments.length ||
        selectedDocuments.some(id => !currentActiveDocuments.includes(id)) ||
        currentActiveDocuments.some(id => !selectedDocuments.includes(id));

      if (!hasChanges) return;

      try {
        // Calculate added and removed documents
        const addedDocuments = selectedDocuments.filter(id => !currentActiveDocuments.includes(id));
        const removedDocuments = currentActiveDocuments.filter(id => !selectedDocuments.includes(id));

        // Add new documents to session
        if (addedDocuments.length > 0) {
          console.log('➕ Adding documents to session:', addedDocuments);
          const updatedSession = await chatAPI.addDocumentsToSession(currentSession.id, addedDocuments);
          setCurrentSession(updatedSession);
        }

        // Remove documents from session
        if (removedDocuments.length > 0) {
          console.log('➖ Removing documents from session:', removedDocuments);
          const updatedSession = await chatAPI.removeDocumentsFromSession(currentSession.id, removedDocuments);
          setCurrentSession(updatedSession);
        }

        console.log(`✅ Session ${currentSession.id} synced with ${selectedDocuments.length} documents`);
      } catch (error) {
        console.error('Failed to sync documents with session:', error);
        toast.error('Failed to update document selection');
      }
    };

    syncDocumentsWithSession();
  }, [selectedDocuments, currentSession?.id]);

  const loadSessions = async () => {
    try {
      // Load sessions filtered by current project
      const userSessions = await chatAPI.getSessions(50, 0, currentProject?.id);
      setSessions(userSessions);

      // If no current session, either select the first existing one or create new
      if (!currentSession) {
        if (userSessions.length > 0) {
          setCurrentSession(userSessions[0]);
        } else {
          await createNewSession();
        }
      }
    } catch (error) {
      console.error('Failed to load sessions:', error);
      toast.error('Failed to load chat history');
    }
  };

  const loadDocuments = async () => {
    try {
      setLoadingDocuments(true);
      // Load documents filtered by current project
      const documents = await chatAPI.getUserDocuments({
        limit: 100,
        project_id: currentProject?.id
      });
      setUserDocuments(documents);

      // Check RAG status for each document
      const statusPromises = documents.map(async (doc) => {
        try {
          const status = await chatAPI.getDocumentRAGStatus(doc.id);
          return { docId: doc.id, isProcessed: status.is_processed };
        } catch (error) {
          return { docId: doc.id, isProcessed: false };
        }
      });

      const statuses = await Promise.allSettled(statusPromises);
      const statusMap: {[key: number]: boolean} = {};

      statuses.forEach((result) => {
        if (result.status === 'fulfilled') {
          statusMap[result.value.docId] = result.value.isProcessed;
        }
      });

      setDocumentRAGStatus(statusMap);
    } catch (error) {
      console.error('Failed to load documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoadingDocuments(false);
    }
  };

  const toggleDocumentSelection = (documentId: number) => {
    setSelectedDocuments(prev =>
      prev.includes(documentId)
        ? prev.filter(id => id !== documentId)
        : [...prev, documentId]
    );
  };

  const selectAllDocuments = () => {
    const filteredDocuments = getFilteredDocuments();
    const allSelected = filteredDocuments.every(doc => selectedDocuments.includes(doc.id));

    if (allSelected) {
      setSelectedDocuments(prev => prev.filter(id => !filteredDocuments.some(doc => doc.id === id)));
    } else {
      setSelectedDocuments(prev => [...Array.from(new Set([...prev, ...filteredDocuments.map(doc => doc.id)]))]);
    }
  };

  const getFilteredDocuments = () => {
    return userDocuments.filter(doc =>
      documentSearchQuery === '' ||
      doc.original_name?.toLowerCase().includes(documentSearchQuery.toLowerCase()) ||
      doc.category?.toLowerCase().includes(documentSearchQuery.toLowerCase()) ||
      doc.project_name?.toLowerCase().includes(documentSearchQuery.toLowerCase())
    );
  };


  const createNewSession = async () => {
    try {
      const session = await chatAPI.createSession({
        title: 'Quick Chat',
        systemPrompt: `You are JARVIS, an intelligent assistant for ${tenantData?.name || 'the organization'}. You have access to uploaded documents and can help with questions about policies, processes, risk management, and compliance. Always cite your sources when referencing document content.`,
        activeDocuments: selectedDocuments,
        project_id: currentProject?.id, // Link session to current project
        settings: {
          selectedAIModel: selectedModel, // ✅ Fixed: use selectedAIModel instead of model
          temperature: 0.7,
          max_tokens: 2000
        }
      });

      setSessions(prev => [session, ...prev]);
      setCurrentSession(session);
      setMessages([]);

      console.log(`✅ Created new chat session with model: ${selectedModel}`);
      return session;
    } catch (error) {
      console.error('Failed to create session:', error);
      toast.error('Failed to create new chat session');
      return null;
    }
  };

  const loadMessages = async () => {
    if (!currentSession) return;

    try {
      const sessionMessages = await chatAPI.getMessages(currentSession.id);
      setMessages(sessionMessages);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const handleSendMessage = async (messageContent: string = inputValue) => {
    if (!messageContent.trim()) return;

    // Ensure we have a session
    let session = currentSession;
    if (!session) {
      session = await createNewSession();
      if (!session) return;
    }

    const userMessage: ChatMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: messageContent.trim(),
      created_at: new Date().toISOString(),
      token_count: Math.ceil(messageContent.length / 4)
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsStreaming(true);

    try {
      // Create streaming assistant message
      const assistantMessage: ChatMessage = {
        id: `temp-assistant-${Date.now()}`,
        role: 'assistant',
        content: '',
        created_at: new Date().toISOString(),
        token_count: 0,
        processing_time_ms: 0
      };

      setMessages(prev => [...prev, assistantMessage]);
      pendingArtifactsRef.current = [];

      await chatAPI.sendMessageStream(
        session.id,
        messageContent.trim(),
        (chunk: string) => {
          setMessages(prev => {
            const updated = [...prev];
            const lastMessage = updated[updated.length - 1];
            if (lastMessage && lastMessage.role === 'assistant') {
              lastMessage.content += chunk;
            }
            return updated;
          });
        },
        (finalMessage: ChatMessage) => {
          const collectedArtifacts = [...pendingArtifactsRef.current];
          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 2] = {
              ...userMessage,
              id: finalMessage.id.replace('-assistant', '')
            };
            updated[updated.length - 1] = {
              ...finalMessage,
              artifacts: collectedArtifacts.length > 0 ? collectedArtifacts : undefined
            };
            return updated;
          });
          pendingArtifactsRef.current = [];
          setIsStreaming(false);
        },
        (error: string) => {
          toast.error('Failed to send message: ' + error);
          setIsStreaming(false);
        },
        (artifact: ChatArtifact) => {
          pendingArtifactsRef.current = [...pendingArtifactsRef.current, artifact];
          setMessages(prev => {
            const updated = [...prev];
            const lastMessage = updated[updated.length - 1];
            if (lastMessage && lastMessage.role === 'assistant') {
              lastMessage.artifacts = [...(lastMessage.artifacts || []), artifact];
            }
            return updated;
          });
        }
      );

    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
      // Remove the temporary user message on error
      setMessages(prev => prev.slice(0, -1));
      setIsStreaming(false);
    }
  };

  const handlePromptClick = (prompt: string) => {
    handleSendMessage(prompt);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    // Ensure we have a session
    let session = currentSession;
    if (!session) {
      session = await createNewSession();
      if (!session) return;
    }

    const newFiles = Array.from(files);
    setUploadedFiles(prev => [...prev, ...newFiles]);

    for (const file of newFiles) {
      try {
        console.log('🔄 Uploading file:', file.name, 'to session:', session.id);

        const result = await chatAPI.uploadDocument(session.id, file);
        console.log('✅ Upload result:', result);

        // Update session with new document
        if (result.session) {
          setCurrentSession(result.session);
        }

        // Update document list to include the new document
        if (result.document) {
          setUserDocuments(prev => [result.document, ...prev]);
          // Auto-select the uploaded document
          setSelectedDocuments(prev => [...prev, result.document.id]);
        }

        // Show confirmation message
        const confirmationMessage: ChatMessage = {
          id: `upload-${Date.now()}`,
          role: 'assistant',
          content: `✅ Successfully uploaded: **${file.name}**. The document is being processed and will be available for questions shortly!`,
          created_at: new Date().toISOString(),
          token_count: 0
        };
        setMessages(prev => [...prev, confirmationMessage]);

        toast.success(`Uploaded ${file.name}`);
      } catch (error) {
        console.error('Upload failed for', file.name, ':', error);

        const axiosError = error as any;
        console.error('Error details:', axiosError.response?.data || axiosError.message);

        const errorMessage = axiosError.response?.data?.message || axiosError.message || 'Unknown upload error';
        toast.error(`Failed to upload ${file.name}: ${errorMessage}`);

        // Remove failed file
        setUploadedFiles(prev => prev.filter(f => f !== file));
      }
    }

    // Reset file input
    if (event.target) {
      event.target.value = '';
    }
  };

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const selectSession = (session: ChatSession) => {
    setCurrentSession(session);
    setMessages([]);
    setUploadedFiles([]);
  };

  const deleteSession = async (sessionId: string) => {
    try {
      await chatAPI.deleteSession(sessionId);
      setSessions(prev => prev.filter(s => s.id !== sessionId));

      if (currentSession?.id === sessionId) {
        const remainingSessions = sessions.filter(s => s.id !== sessionId);
        if (remainingSessions.length > 0) {
          setCurrentSession(remainingSessions[0]);
        } else {
          await createNewSession();
        }
      }

      toast.success('Chat deleted');
    } catch (error) {
      console.error('Failed to delete session:', error);
      toast.error('Failed to delete chat');
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Just now';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Just now';

    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffMinutes = Math.floor(diffTime / (1000 * 60));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    // Less than 1 minute
    if (diffMinutes < 1) return 'Just now';

    // Less than 1 hour
    if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;

    // Less than 24 hours
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;

    // Yesterday
    if (diffDays === 1) return 'Yesterday';

    // Less than 7 days
    if (diffDays < 7) return `${diffDays} days ago`;

    // Less than 30 days
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;

    // Older - show date
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;

    setMouseDown(true);
    setDragStart({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      startX: e.clientX,
      startY: e.clientY
    });
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!mouseDown) return;

    // Start dragging only after moving more than 5 pixels
    const dx = Math.abs(e.clientX - dragStart.startX);
    const dy = Math.abs(e.clientY - dragStart.startY);

    if (!isDragging && (dx > 5 || dy > 5)) {
      setIsDragging(true);
    }

    if (!isDragging) return;

    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;

    // Keep button within viewport bounds (80px is button size)
    const maxX = window.innerWidth - 80;
    const maxY = window.innerHeight - 80;

    setButtonPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY))
    });
  };

  const handleMouseUp = () => {
    setMouseDown(false);
    setTimeout(() => {
      setIsDragging(false);
    }, 50);
  };

  useEffect(() => {
    if (mouseDown) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [mouseDown, isDragging, dragStart]);

  // Close model selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showModelSelector && !target.closest('.model-selector-container')) {
        setShowModelSelector(false);
      }
    };

    if (showModelSelector) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showModelSelector]);

  return (
    <>
      {/* Jarvis Chat Button - Header Only (Draggable) */}
      <div
        ref={buttonRef}
        className="fixed z-50"
        style={{
          ...(buttonPosition.x === 0 && buttonPosition.y === 0
            ? { top: '16px', right: '280px' } // Default header position (before Help icon)
            : { left: `${buttonPosition.x}px`, top: `${buttonPosition.y}px` }),
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none'
        }}
        onMouseDown={handleMouseDown}
      >
        <button
          onClick={() => {
            if (!isDragging) {
              setIsOpen(true);
            }
          }}
          className={`group relative text-white p-3 rounded-full shadow-2xl transition-all duration-300 transform hover:scale-110 ${
            isOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100'
          }`}
          style={{
            background: 'linear-gradient(135deg, #7f27c6 0%, #f30388 100%)',
            boxShadow: '0 20px 40px rgba(127, 39, 198, 0.4), 0 0 30px rgba(243, 3, 136, 0.3)'
          }}
          title="Ask Jarvis AI"
        >
          <MessageCircle className="w-5 h-5" />
          <div className="absolute -top-1 -right-1 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center animate-pulse"
               style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)' }}>
            AI
          </div>

          {/* Glowing animation */}
          <div className="absolute inset-0 rounded-full opacity-20 animate-ping"
               style={{ background: 'linear-gradient(135deg, #7f27c6 0%, #f30388 100%)' }}></div>
        </button>
      </div>

      {/* Chat Interface */}
      <div
        className={`fixed inset-0 z-40 transition-all duration-500 ease-in-out ${
          isOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
        style={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(20px)'
        }}
      >
        {/* Blinking Light Particles */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full animate-pulse"
              style={{
                background: 'linear-gradient(135deg, #15baf8, #7134ce)',
                left: `${10 + (i * 7) % 80}%`,
                top: `${15 + (i * 11) % 70}%`,
                animationDelay: `${i * 0.3}s`,
                animationDuration: `${2 + (i % 3)}s`,
                opacity: 0.4 + (i % 3) * 0.2
              }}
            />
          ))}
          {[...Array(8)].map((_, i) => (
            <div
              key={`sparkle-${i}`}
              className="absolute w-1 h-1 rounded-full animate-ping"
              style={{
                background: '#ffffff',
                left: `${20 + (i * 9) % 60}%`,
                top: `${25 + (i * 13) % 50}%`,
                animationDelay: `${i * 0.7}s`,
                animationDuration: `${1.5 + (i % 2)}s`,
                opacity: 0.6
              }}
            />
          ))}
        </div>
        <div className="flex h-full">
          {/* Chat History Sidebar - LEFT */}
          <div className={`${showHistory ? 'w-80' : 'w-0'} transition-all duration-300 overflow-hidden bg-gray-50 border-r border-gray-200`}>
            {showHistory && (
              <div className="h-full flex flex-col">
                {/* History Header */}
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Chat History</h3>
                    <button
                      onClick={() => createNewSession()}
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                      title="New Chat"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Sessions List */}
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  {sessions.map((session) => (
                    <div
                      key={session.id}
                      onClick={() => selectSession(session)}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        currentSession?.id === session.id
                          ? 'bg-blue-100 border border-blue-300'
                          : 'bg-white hover:bg-gray-100 border border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-gray-900 truncate">
                            {session.title}
                          </h4>
                          <div className="flex items-center space-x-2 mt-1">
                            <Clock className="w-3 h-3 text-gray-400" />
                            <span className="text-xs text-gray-500">
                              {formatDate(session.updated_at || session.created_at)}
                            </span>
                          </div>
                          {session.active_documents && session.active_documents.length > 0 && (
                            <div className="flex items-center space-x-1 mt-1">
                              <FileText className="w-3 h-3 text-blue-500" />
                              <span className="text-xs text-blue-600">
                                {session.active_documents.length} docs
                              </span>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteSession(session.id);
                          }}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                          title="Delete Chat"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Chat Container */}
          <div
            className={`flex-1 bg-white shadow-2xl transition-all duration-500 ease-in-out transform ${
              isOpen
                ? 'translate-x-0 opacity-100'
                : 'translate-x-full opacity-0'
            }`}
            style={{
              boxShadow: '-20px 0 60px rgba(0, 0, 0, 0.3)'
            }}
          >
            {/* Header */}
            <div className="text-white px-6 py-4 flex items-center justify-between"
                 style={{ background: 'linear-gradient(135deg, #7f27c6 0%, #f30388 100%)' }}>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="bg-white/20 hover:bg-white/30 p-2 rounded-lg transition-colors"
                  title="Toggle Chat History"
                >
                  <Clock className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setShowDocuments(!showDocuments)}
                  className="bg-white/20 hover:bg-white/30 p-2 rounded-lg transition-colors relative"
                  title="Select Documents"
                >
                  <Folder className="w-5 h-5" />
                  {selectedDocuments.length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {selectedDocuments.length}
                    </span>
                  )}
                </button>
                <div className="bg-white/20 p-2 rounded-lg">
                  <Zap className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Ask Jarvis</h2>
                  <p className="text-white/80 text-sm">AI-powered document analysis</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {/* AI Model Selector */}
                <div className="relative model-selector-container">
                  <button
                    onClick={() => setShowModelSelector(!showModelSelector)}
                    className="bg-white/20 hover:bg-white/30 px-3 py-2 rounded-lg transition-colors flex items-center space-x-2"
                    title="Select AI Model"
                  >
                    <span className="text-sm font-medium">
                      {availableModels.find(m => m.id === selectedModel)?.name || 'Select Model'}
                    </span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Model Dropdown */}
                  {showModelSelector && (
                    <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto">
                      <div className="p-3 border-b border-gray-200 bg-gray-50">
                        <h4 className="font-semibold text-gray-900 text-sm">Select AI Model</h4>
                        <p className="text-xs text-gray-600 mt-1">Choose your preferred AI model</p>
                      </div>
                      <div className="py-2">
                        {availableModels.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-gray-500 text-center">
                            No models available
                          </div>
                        ) : (
                          availableModels.map((model) => (
                            <button
                              key={model.id}
                              onClick={() => {
                                setSelectedModel(model.id);
                                setShowModelSelector(false);
                                toast.success(`Switched to ${model.name}`);
                              }}
                              className={`w-full px-4 py-3 text-left hover:bg-gray-100 transition-colors ${
                                selectedModel === model.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2">
                                    <span className="font-medium text-gray-900 text-sm">{model.name}</span>
                                    {selectedModel === model.id && (
                                      <Check className="w-4 h-4 text-blue-600" />
                                    )}
                                  </div>
                                  <div className="flex items-center space-x-2 mt-1">
                                    <span className="text-xs text-gray-600">{model.provider}</span>
                                    {model.available && (
                                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                        Enabled
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1">
                                    Max tokens: {model.maxTokens?.toLocaleString() || 'N/A'}
                                  </div>
                                </div>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => createNewSession()}
                  className="bg-white/20 hover:bg-white/30 p-2 rounded-lg transition-colors"
                  title="New Chat"
                >
                  <Plus className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="bg-white/20 hover:bg-white/30 p-2 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Messages Container */}
            <div className="flex-1 overflow-hidden flex flex-col" style={{ height: 'calc(100vh - 84px)' }}>
              {messages.length === 0 ? (
                // Welcome Screen with Predefined Prompts
                <div className="flex-1 p-6 overflow-y-auto">
                  <div className="max-w-4xl mx-auto">
                    {/* Welcome Message */}
                    <div className="text-center mb-8">
                      <div className="p-6 rounded-2xl mb-6"
                           style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(139, 92, 246, 0.1) 50%, rgba(236, 72, 153, 0.1) 100%)' }}>
                        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                             style={{ background: 'linear-gradient(135deg, #15baf8 0%, #7134ce 100%)' }}>
                          <BookOpen className="w-8 h-8 text-white" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-2">Welcome to Jarvis AI</h3>
                        <p className="text-gray-600 max-w-2xl mx-auto">
                          Your intelligent assistant for RCSA document analysis. I can help you with risk analysis,
                          compliance checks, process optimization, and more based on your uploaded documents.
                        </p>
                      </div>
                    </div>

                    {/* Predefined Prompts Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                      {predefinedPrompts.map((prompt, index) => (
                        <button
                          key={index}
                          onClick={() => handlePromptClick(prompt.prompt)}
                          className="group bg-white border-2 border-gray-200 p-4 rounded-xl transition-all duration-300 hover:shadow-lg hover:-translate-y-1 text-left"
                          style={{
                            borderColor: 'rgba(59, 130, 246, 0.2)'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.borderColor = '#3B82F6'}
                          onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.2)'}
                        >
                          <div className="flex items-center space-x-3 mb-2">
                            <div className="p-2 rounded-lg group-hover:scale-110 transition-transform"
                                 style={{ background: 'linear-gradient(135deg, #15baf8 0%, #7134ce 100%)' }}>
                              <prompt.icon className="w-5 h-5 text-white" />
                            </div>
                            <h4 className="font-semibold text-gray-800 transition-colors"
                                style={{ color: 'inherit' }}
                                onMouseEnter={(e) => e.currentTarget.style.color = '#3B82F6'}
                                onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}>
                              {prompt.title}
                            </h4>
                          </div>
                          <p className="text-sm text-gray-600 group-hover:text-gray-700">
                            {prompt.description}
                          </p>
                        </button>
                      ))}
                    </div>

                    {/* Quick Actions */}
                    <div className="bg-gray-50 p-4 rounded-xl">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                        <Plus className="w-4 h-4 mr-2" />
                        Quick Actions
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        <button
                                className="px-3 py-1 rounded-full text-sm transition-colors flex items-center gap-1"
                                style={{
                                  backgroundColor: 'rgba(21, 186, 248, 0.1)',
                                  color: '#15baf8'
                                }}
                                onClick={() => fileInputRef.current?.click()}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(21, 186, 248, 0.2)'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(21, 186, 248, 0.1)'}>
                          <Upload className="w-3 h-3" />
                          Upload Documents
                        </button>
                        <button className="px-3 py-1 rounded-full text-sm transition-colors"
                                style={{
                                  backgroundColor: 'rgba(139, 92, 246, 0.1)',
                                  color: '#8B5CF6'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.2)'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.1)'}>
                          View Templates
                        </button>
                        <button className="px-3 py-1 rounded-full text-sm transition-colors"
                                style={{
                                  backgroundColor: 'rgba(16, 185, 129, 0.1)',
                                  color: '#10B981'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.2)'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.1)'}>
                          Export Analysis
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                // Messages View
                <div className="flex-1 overflow-y-auto p-6">
                  <div className="max-w-4xl mx-auto space-y-4">
                    {messages.map((message, index) => (
                      <div
                        key={message.id ? `jarvis-msg-${message.id}` : `jarvis-temp-${index}-${Date.now()}`}
                        className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-2xl p-4 rounded-2xl ${
                            message.role === 'user'
                              ? 'text-white'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                          style={message.role === 'user' ? {
                            background: 'linear-gradient(135deg, #15baf8 0%, #7134ce 100%)'
                          } : {}}
                      >
                        <div className="text-sm leading-relaxed">
                          {message.content.split('\n').map((line, index) => {
                            // Handle bold text
                            if (line.includes('**')) {
                              const parts = line.split('**');
                              return (
                                <p key={index} className="mb-1">
                                  {parts.map((part, i) =>
                                    i % 2 === 1 ? <strong key={i}>{part}</strong> : part
                                  )}
                                </p>
                              );
                            }
                            return line ? <p key={index} className="mb-1">{line}</p> : <br key={index} />;
                          })}
                        </div>
                        <div className={`text-xs mt-2 opacity-70 flex items-center space-x-2 ${
                          message.role === 'user' ? 'text-white' : 'text-gray-500'
                        }`}>
                          <span>{new Date(message.created_at).toLocaleTimeString()}</span>
                          {message.token_count && (
                            <>
                              <span>•</span>
                              <span>{message.token_count} tokens</span>
                            </>
                          )}
                          {message.processing_time_ms && (
                            <>
                              <span>•</span>
                              <span>{message.processing_time_ms}ms</span>
                            </>
                          )}
                        </div>

                        {/* File Artifacts — download buttons */}
                        {message.artifacts && message.artifacts.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="text-xs font-medium text-gray-600 mb-2">Generated Files:</div>
                            <div className="space-y-2">
                              {message.artifacts.map((artifact) => (
                                <button
                                  key={artifact.fileId}
                                  onClick={() => chatAPI.downloadFile(artifact.fileId, artifact.filename)}
                                  className="flex items-center gap-2 w-full px-3 py-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg text-left transition-colors"
                                >
                                  <span className="text-lg">{artifact.icon}</span>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs font-medium text-blue-800 truncate">{artifact.filename}</div>
                                    <div className="text-xs text-blue-600">{artifact.label}</div>
                                  </div>
                                  <Download className="w-4 h-4 text-blue-600 flex-shrink-0" />
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Document References */}
                        {message.document_references && message.document_references.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="text-xs font-medium text-gray-600 mb-2">
                              Sources:
                            </div>
                            <div className="space-y-1">
                              {message.document_references.map((ref) => (
                                <div
                                  key={ref.id}
                                  className="flex items-center space-x-2 text-xs bg-white/20 rounded p-2"
                                >
                                  <FileText className="w-3 h-3 text-gray-500 flex-shrink-0" />
                                  <span className="truncate flex-1">{ref.document?.original_filename || ref.document?.filename || 'Unknown'}</span>
                                  <span className="text-gray-500">
                                    {Math.round(ref.relevance_score * 100)}%
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {isStreaming && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 p-4 rounded-2xl">
                        <div className="flex space-x-2">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                  </div>
                </div>
              )}

              {/* Input Area */}
              <div className="border-t bg-white p-4">
                <div className="max-w-4xl mx-auto">
                  {/* Uploaded Files Display */}
                  {uploadedFiles.length > 0 && (
                    <div className="mb-3">
                      <div className="flex flex-wrap gap-2">
                        {uploadedFiles.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-2 px-3 py-1 rounded-full text-sm"
                            style={{
                              background: 'linear-gradient(135deg, rgba(21, 186, 248, 0.1) 0%, rgba(113, 52, 206, 0.1) 100%)',
                              border: '1px solid rgba(21, 186, 248, 0.2)'
                            }}
                          >
                            <File className="w-3 h-3" style={{ color: '#15baf8' }} />
                            <span className="text-gray-700 max-w-32 truncate">{file.name}</span>
                            <button
                              onClick={() => removeFile(index)}
                              className="text-gray-400 hover:text-red-500 transition-colors"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="relative">
                    {/* Hidden file input */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,.png,.jpg,.jpeg,.gif,.bmp,.webp"
                      onChange={handleFileUpload}
                      className="hidden"
                    />

                    {/* ChatGPT-style input container */}
                    <div className="relative bg-white rounded-full border border-gray-300 shadow-sm hover:shadow-md transition-all duration-300 focus-within:shadow-lg">
                      <div className="flex items-center px-4 py-3">
                        {/* Plus icon for file upload */}
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="flex-shrink-0 p-1 rounded-full hover:bg-gray-100 transition-colors mr-3"
                        >
                          <Plus className="w-5 h-5 text-gray-500" />
                        </button>

                        {/* Input field */}
                        <input
                          ref={inputRef}
                          type="text"
                          value={inputValue}
                          onChange={(e) => setInputValue(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                          placeholder="Ask anything about your documents..."
                          className="flex-1 bg-transparent focus:outline-none text-gray-900 placeholder-gray-500"
                        />

                        {/* Send button */}
                        <button
                          onClick={() => handleSendMessage()}
                          disabled={!inputValue.trim()}
                          className="flex-shrink-0 p-2 rounded-full ml-2 transition-all duration-300 disabled:opacity-30"
                          style={{
                            background: inputValue.trim()
                              ? 'linear-gradient(135deg, #15baf8 0%, #7134ce 100%)'
                              : 'transparent'
                          }}
                        >
                          <Send
                            className="w-4 h-4"
                            style={{
                              color: inputValue.trim() ? '#ffffff' : '#9CA3AF'
                            }}
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Document Selection Sidebar - RIGHT */}
          <div className={`${showDocuments ? 'w-80' : 'w-0'} transition-all duration-300 overflow-hidden bg-gray-50 border-l border-gray-200`}>
            {showDocuments && (
              <div className="h-full flex flex-col">
                {/* Documents Header */}
                <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-indigo-600 to-purple-600">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-white">Documents</h3>
                    <div className="flex items-center space-x-2">
                      {selectedDocuments.length > 0 && (
                        <span className="bg-white/20 text-white text-xs px-2 py-1 rounded-full">
                          {selectedDocuments.length} selected
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Search */}
                  <div className="mt-3 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/70 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search documents..."
                      value={documentSearchQuery}
                      onChange={(e) => setDocumentSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 bg-white/20 text-white placeholder-white/70 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50"
                    />
                  </div>
                  {/* Action Buttons */}
                  <div className="mt-2 space-y-2">
                    <button
                      onClick={selectAllDocuments}
                      className="w-full bg-white/20 hover:bg-white/30 text-white py-2 px-3 rounded-lg text-sm transition-colors"
                    >
                      {getFilteredDocuments().every(doc => selectedDocuments.includes(doc.id)) && getFilteredDocuments().length > 0
                        ? 'Deselect All' : 'Select All'}
                    </button>
                    {selectedDocuments.length > 0 && (
                      <div className="text-sm text-white/80 bg-white/10 rounded-lg p-3">
                        {selectedDocuments.length} document{selectedDocuments.length > 1 ? 's' : ''} selected for JARVIS
                      </div>
                    )}
                  </div>
                </div>

                {/* Documents List */}
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  {loadingDocuments ? (
                    <div className="text-center text-gray-500 py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="mt-2">Loading documents...</p>
                    </div>
                  ) : getFilteredDocuments().length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                      <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p className="text-lg font-medium">No documents found</p>
                      <p className="text-sm mt-1">
                        {documentSearchQuery ? 'Try a different search term' : 'Upload documents to get started'}
                      </p>
                    </div>
                  ) : (
                    getFilteredDocuments().map((doc) => (
                      <div
                        key={doc.id}
                        onClick={() => toggleDocumentSelection(doc.id)}
                        className={`p-3 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                          selectedDocuments.includes(doc.id)
                            ? 'bg-blue-100 border-blue-300 ring-2 ring-blue-200'
                            : 'bg-white border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
                              <h4 className="font-medium text-gray-900 truncate">
                                {doc.original_name || doc.filename}
                              </h4>
                              {selectedDocuments.includes(doc.id) && (
                                <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
                              )}
                            </div>
                            <p className="text-sm text-gray-500 mt-1">{doc.category}</p>
                            <p className="text-xs text-gray-400 mt-1">{doc.project_name}</p>
                            <div className="flex items-center space-x-3 mt-2 text-xs text-gray-400">
                              <span>{chatAPI.formatFileSize(doc.file_size)}</span>
                              <span>•</span>
                              <span>{chatAPI.formatDate(doc.upload_date)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AskJarvis;